import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { observer } from 'mobx-react-lite';
import type { CitationResponse, SourceResponse } from 'we-tree-api-client/v2';

import { useSuggestionsStore } from '@/hooks/useStores';
import { getDiffPathLabel, type SuggestionEvidenceDraft } from '@/types/suggestion';

import './suggest-evidence-step.css';

const CREATE_NEW_SOURCE = '__new_source__';
const CREATE_NEW_CITATION = '__new_citation__';

export interface SuggestEvidenceStepProps {
  familyId: string;
  targetPaths: string[];
  drafts: SuggestionEvidenceDraft[];
  onChange: (drafts: SuggestionEvidenceDraft[]) => void;
}

const SuggestEvidenceStep: React.FC<SuggestEvidenceStepProps> = observer(
  ({ familyId, targetPaths, drafts, onChange }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const suggestionsStore = useSuggestionsStore();

    const [sources, setSources] = useState<SourceResponse[]>([]);
    const [citations, setCitations] = useState<CitationResponse[]>([]);
    const [loadingSources, setLoadingSources] = useState(false);
    const [loadingCitations, setLoadingCitations] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    const [sourceMode, setSourceMode] = useState<string>('');
    const [newSourceTitle, setNewSourceTitle] = useState('');
    const [newSourceUrl, setNewSourceUrl] = useState('');
    const [citationMode, setCitationMode] = useState<string>('');
    const [detailLocator, setDetailLocator] = useState('');
    const [notes, setNotes] = useState('');
    const [mediaAssetId, setMediaAssetId] = useState<string | null>(null);
    const [mediaFileName, setMediaFileName] = useState<string | null>(null);
    const [targetPath, setTargetPath] = useState(targetPaths[0] ?? '');

    useEffect(() => {
      let cancelled = false;
      const load = async () => {
        setLoadingSources(true);
        setLocalError(null);
        const result = await suggestionsStore.listEvidenceSources(familyId);
        if (cancelled) return;
        setLoadingSources(false);
        if (result.success) {
          setSources(result.data);
        } else {
          setSources([]);
          setLocalError(result.error?.message ?? t('evidence.errors.loadSources'));
        }
      };
      void load();
      return () => {
        cancelled = true;
      };
    }, [familyId, suggestionsStore, t]);

    useEffect(() => {
      if (!sourceMode || sourceMode === CREATE_NEW_SOURCE) {
        setCitations([]);
        setCitationMode('');
        return;
      }
      let cancelled = false;
      const load = async () => {
        setLoadingCitations(true);
        const result = await suggestionsStore.listEvidenceCitationsForSource(familyId, sourceMode);
        if (cancelled) return;
        setLoadingCitations(false);
        if (result.success) {
          setCitations(result.data);
        } else {
          setCitations([]);
        }
        setCitationMode('');
      };
      void load();
      return () => {
        cancelled = true;
      };
    }, [familyId, sourceMode, suggestionsStore]);

    useEffect(() => {
      if (!targetPath && targetPaths[0]) {
        setTargetPath(targetPaths[0]);
      }
    }, [targetPath, targetPaths]);

    const resetComposer = () => {
      setSourceMode('');
      setNewSourceTitle('');
      setNewSourceUrl('');
      setCitationMode('');
      setDetailLocator('');
      setNotes('');
      setMediaAssetId(null);
      setMediaFileName(null);
      setTargetPath(targetPaths[0] ?? '');
      setLocalError(null);
    };

    const citationLabel = (c: CitationResponse): string => {
      const part = c.detailLocator || c.notes || '';
      return part ? `${part.slice(0, 60)}${part.length > 60 ? '…' : ''}` : t('evidence.citationNoLocator');
    };

    const handleUpload = async (file: File | undefined) => {
      if (!file) return;
      setUploading(true);
      setLocalError(null);
      const result = await suggestionsStore.uploadEvidenceMedia(familyId, file);
      setUploading(false);
      if (result.success && result.data) {
        setMediaAssetId(result.data.id);
        setMediaFileName(file.name);
      } else {
        setLocalError(result.error?.message ?? t('evidence.errors.uploadFailed'));
      }
    };

    const handleAddDraft = () => {
      setLocalError(null);

      if (!sourceMode) {
        setLocalError(t('evidence.errors.selectSource'));
        return;
      }

      if (sourceMode === CREATE_NEW_SOURCE) {
        if (!newSourceTitle.trim() || !newSourceUrl.trim()) {
          setLocalError(t('evidence.errors.websiteNeedsTitleUrl'));
          return;
        }
        onChange([
          ...drafts,
          {
            newSource: { title: newSourceTitle.trim(), url: newSourceUrl.trim() },
            newCitation: {
              detailLocator: detailLocator.trim() || undefined,
              notes: notes.trim() || undefined,
              mediaAssetId: mediaAssetId || undefined
            },
            targetPath: targetPath || undefined
          }
        ]);
        resetComposer();
        return;
      }

      if (!citationMode) {
        setLocalError(t('evidence.errors.selectCitation'));
        return;
      }

      if (citationMode === CREATE_NEW_CITATION) {
        onChange([
          ...drafts,
          {
            sourceId: sourceMode,
            newCitation: {
              detailLocator: detailLocator.trim() || undefined,
              notes: notes.trim() || undefined,
              mediaAssetId: mediaAssetId || undefined
            },
            targetPath: targetPath || undefined
          }
        ]);
        resetComposer();
        return;
      }

      onChange([
        ...drafts,
        {
          sourceId: sourceMode,
          citationId: citationMode,
          targetPath: targetPath || undefined
        }
      ]);
      resetComposer();
    };

    const removeDraft = (index: number) => {
      onChange(drafts.filter((_, i) => i !== index));
    };

    const draftSummary = (draft: SuggestionEvidenceDraft): string => {
      const pathSuffix = draft.targetPath ? ` → ${getDiffPathLabel(draft.targetPath)}` : '';
      if (draft.newSource) {
        return t('evidence.draftNewWebsite', { title: draft.newSource.title, pathSuffix });
      }
      const source = sources.find((s) => s.id === draft.sourceId);
      const title = source?.title ?? t('evidence.sourceFallback');
      if (draft.citationId) {
        return t('evidence.draftExistingCitation', { title, pathSuffix });
      }
      return t('evidence.draftNewCitation', { title, pathSuffix });
    };

    const showNewCitationFields = sourceMode === CREATE_NEW_SOURCE || citationMode === CREATE_NEW_CITATION;

    return (
      <div className="suggest-evidence-step" data-tour="suggest-evidence">
        <h3 className="suggest-evidence-step__title">{t('evidence.title')}</h3>
        <p className="suggest-evidence-step__lede">{t('evidence.lede')}</p>

        {localError && (
          <div className="suggest-evidence-step__error" role="alert">
            {localError}
          </div>
        )}

        {drafts.length > 0 && (
          <ul className="suggest-evidence-step__queued" aria-label={t('evidence.queuedAria')}>
            {drafts.map((draft, index) => (
              <li key={`draft-${index}`} className="suggest-evidence-step__queued-item">
                <span>{draftSummary(draft)}</span>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => removeDraft(index)}>
                  {tCommon('remove')}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="suggest-evidence-step__form">
          <div className="suggest-evidence-step__field">
            <label className="suggest-evidence-step__label" htmlFor="suggest-evidence-source">
              {t('evidence.source')}
            </label>
            <Select.Root
              value={sourceMode || undefined}
              onValueChange={(v) => setSourceMode(v)}
              disabled={loadingSources}
            >
              <Select.Trigger
                id="suggest-evidence-source"
                className="form-select-trigger"
                aria-label={t('evidence.selectSourceAria')}
              >
                <Select.Value
                  placeholder={loadingSources ? t('evidence.loadingSources') : t('evidence.selectSourcePlaceholder')}
                />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                  <Select.Item value={CREATE_NEW_SOURCE} className="form-select-item">
                    <Select.ItemText>{t('evidence.createWebsiteSource')}</Select.ItemText>
                  </Select.Item>
                  {sources.map((s) => (
                    <Select.Item key={s.id} value={s.id} className="form-select-item">
                      <Select.ItemText>
                        {s.title}
                        {s.type ? ` (${s.type})` : ''}
                      </Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>

          {sourceMode === CREATE_NEW_SOURCE && (
            <div className="suggest-evidence-step__field-group">
              <div className="suggest-evidence-step__field">
                <label className="suggest-evidence-step__label" htmlFor="suggest-evidence-source-title">
                  {t('evidence.websiteTitle')}
                </label>
                <input
                  id="suggest-evidence-source-title"
                  type="text"
                  className="suggest-evidence-step__input"
                  value={newSourceTitle}
                  onChange={(e) => setNewSourceTitle(e.target.value)}
                  placeholder={t('evidence.websiteTitlePlaceholder')}
                />
              </div>
              <div className="suggest-evidence-step__field">
                <label className="suggest-evidence-step__label" htmlFor="suggest-evidence-source-url">
                  {t('sources.url')}
                </label>
                <input
                  id="suggest-evidence-source-url"
                  type="url"
                  className="suggest-evidence-step__input"
                  value={newSourceUrl}
                  onChange={(e) => setNewSourceUrl(e.target.value)}
                  placeholder="https://"
                />
              </div>
            </div>
          )}

          {sourceMode && sourceMode !== CREATE_NEW_SOURCE && (
            <div className="suggest-evidence-step__field">
              <label className="suggest-evidence-step__label" htmlFor="suggest-evidence-citation">
                {t('evidence.citation')}
              </label>
              <Select.Root
                value={citationMode || undefined}
                onValueChange={(v) => setCitationMode(v)}
                disabled={loadingCitations}
              >
                <Select.Trigger
                  id="suggest-evidence-citation"
                  className="form-select-trigger"
                  aria-label={t('evidence.selectCitationAria')}
                >
                  <Select.Value
                    placeholder={
                      loadingCitations ? t('evidence.loadingCitations') : t('evidence.selectCitationPlaceholder')
                    }
                  />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                    <Select.Item value={CREATE_NEW_CITATION} className="form-select-item">
                      <Select.ItemText>{t('evidence.createNewCitation')}</Select.ItemText>
                    </Select.Item>
                    {citations.map((c) => (
                      <Select.Item key={c.id} value={c.id} className="form-select-item">
                        <Select.ItemText>{citationLabel(c)}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>
          )}

          {showNewCitationFields && (
            <div className="suggest-evidence-step__field-group">
              <div className="suggest-evidence-step__field">
                <label className="suggest-evidence-step__label" htmlFor="suggest-evidence-locator">
                  {t('evidence.locator')}
                </label>
                <input
                  id="suggest-evidence-locator"
                  type="text"
                  className="suggest-evidence-step__input"
                  value={detailLocator}
                  onChange={(e) => setDetailLocator(e.target.value)}
                />
              </div>
              <div className="suggest-evidence-step__field">
                <label className="suggest-evidence-step__label" htmlFor="suggest-evidence-notes">
                  {t('person.notes')}
                </label>
                <input
                  id="suggest-evidence-notes"
                  type="text"
                  className="suggest-evidence-step__input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              <div className="suggest-evidence-step__field">
                <label className="suggest-evidence-step__label" htmlFor="suggest-evidence-media">
                  {t('evidence.imageEvidence')}
                </label>
                <input
                  id="suggest-evidence-media"
                  type="file"
                  accept="image/*"
                  disabled={uploading}
                  onChange={(e) => void handleUpload(e.target.files?.[0])}
                />
                {mediaFileName && (
                  <p className="suggest-evidence-step__media-name">
                    {t('evidence.attached', { fileName: mediaFileName })}
                  </p>
                )}
                {uploading && <p className="suggest-evidence-step__hint">{t('evidence.uploading')}</p>}
              </div>
            </div>
          )}

          {targetPaths.length > 0 && (
            <div className="suggest-evidence-step__field">
              <label className="suggest-evidence-step__label" htmlFor="suggest-evidence-path">
                {t('evidence.supportsChange')}
              </label>
              <Select.Root value={targetPath || undefined} onValueChange={(v) => setTargetPath(v)}>
                <Select.Trigger
                  id="suggest-evidence-path"
                  className="form-select-trigger"
                  aria-label={t('evidence.targetFieldAria')}
                >
                  <Select.Value placeholder={t('evidence.targetFieldPlaceholder')} />
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                    {targetPaths.map((path) => (
                      <Select.Item key={path} value={path} className="form-select-item">
                        <Select.ItemText>{getDiffPathLabel(path)}</Select.ItemText>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </div>
          )}

          <button type="button" className="btn btn--secondary btn--sm" onClick={handleAddDraft} disabled={uploading}>
            {t('actions.addEvidence')}
          </button>
        </div>
      </div>
    );
  }
);

export default SuggestEvidenceStep;
